import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useCalendarContext, isFullPageCalendarView } from '../../context/CalendarContext';
import { useEventContext } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from '../../i18n/LanguageContext';
import { usePlants } from '../../hooks/usePlants';
import { useSavedTodos } from '../../hooks/useSavedTodos';
import {
  TOMATO_STARTER_TASKS,
  buildStarterEvents,
  expandStarterPlan,
  shouldOfferStarterPlan,
  writeStarterChoice,
} from '../../data/starterPlan';
import { saveStarterEvents } from '../../services/saveStarterEvents';
import Icon from '../common/Icon';
import logo from '../../assets/logo.png';
import './StarterPlanOffer.css';

function taskWhen(task, t) {
  if (task.whenKey) return t(task.whenKey);
  if (task.week === 1) return t('starter.today');
  return t('starter.week', { week: task.week });
}

function matchingPlant(plants, category) {
  const wanted = category.trim().toLowerCase();
  return (plants || []).find(
    (plant) => (plant.category || '').trim().toLowerCase() === wanted
  );
}

export default function StarterPlanOffer() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { eventsReady, savedEvents } = useEventContext();
  const { currentView, setCurrentView, goToToday } = useCalendarContext();
  const { showError } = useToast();
  const { plants, isLoading: plantsLoading, addPlantMutation } = usePlants(currentUser?.uid);
  const { savedItems, setItems } = useSavedTodos();
  const queryClient = useQueryClient();
  const addButtonRef = useRef(null);
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);

  const startDate = useMemo(() => new Date(), []);
  const plan = useMemo(() => expandStarterPlan(startDate), [startDate]);
  const visible =
    !dismissed &&
    shouldOfferStarterPlan({
      user: currentUser,
      eventsReady,
      eventCount: savedEvents.length,
    });

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape' && !saving) {
        writeStarterChoice(currentUser.uid, 'empty');
        setDismissed(true);
      }
    };
    document.addEventListener('keydown', onKey);
    addButtonRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, saving, currentUser?.uid]);

  if (!visible) return null;

  const startEmpty = () => {
    if (saving) return;
    writeStarterChoice(currentUser.uid, 'empty');
    setDismissed(true);
  };

  const addPlan = async () => {
    if (saving || plantsLoading) return;
    setSaving(true);
    try {
      const category = t('starter.plantCategory');
      let plant = matchingPlant(plants, category);
      if (!plant) {
        plant = await addPlantMutation.mutateAsync({
          category,
          variety: '',
          icon: 'tomato',
        });
      }
      const events = buildStarterEvents(startDate, {
        plantId: plant.id,
        nameFor: (row) => t(row.nameKey),
      });
      const saved = await saveStarterEvents(currentUser.uid, events);
      queryClient.setQueryData(['events', currentUser.uid], (existing = []) => [
        ...existing,
        ...saved,
      ]);
      queryClient.invalidateQueries({ queryKey: ['events', currentUser.uid] });
      const names = plan.rows.map((row) => t(row.nameKey));
      const merged = [...names];
      for (const item of savedItems) {
        if (!merged.some((name) => name.toLowerCase() === item.toLowerCase())) {
          merged.push(item);
        }
      }
      setItems(merged);
      writeStarterChoice(currentUser.uid, 'added');
      setDismissed(true);
      if (isFullPageCalendarView(currentView)) {
        setCurrentView('daily');
      }
      goToToday();
    } catch {
      showError(t('starter.addFailed'));
      setSaving(false);
    }
  };

  return createPortal(
    <div className="starter-plan-backdrop">
      <div
        className="starter-plan-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="starter-plan-title"
      >
        <img src={logo} alt="" className="starter-plan-logo" />
        <h1 id="starter-plan-title" className="starter-plan-title">
          {t('starter.fillTitle')}
        </h1>
        <p className="starter-plan-body">{t('starter.fillBody')}</p>

        <div className="starter-plan-sheet">
          <div className="starter-plan-head">
            <span className="starter-plan-badge starter-plan-badge--plant" aria-hidden>
              <Icon plantIcon="tomato" />
            </span>
            <div>
              <div className="starter-plan-name">{t('starter.tomatoesFromSeed')}</div>
              <div className="starter-plan-meta">
                {t('starter.planSummary', {
                  weeks: plan.weeks,
                  count: plan.occurrences.length,
                })}
              </div>
            </div>
          </div>
          <ul className="starter-plan-tasks">
            {TOMATO_STARTER_TASKS.map((task) => (
              <li key={task.id} className="starter-plan-task">
                <span className="starter-plan-badge" aria-hidden>
                  <Icon name={task.icon} />
                </span>
                <span className="starter-plan-task-name">{t(task.nameKey)}</span>
                <span className="starter-plan-task-when">{taskWhen(task, t)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="starter-plan-actions">
          <button
            ref={addButtonRef}
            type="button"
            className="starter-plan-add"
            onClick={addPlan}
            disabled={saving || plantsLoading}
          >
            {saving ? t('starter.adding') : t('starter.addPlan')}
          </button>
          <button type="button" className="starter-plan-skip" onClick={startEmpty} disabled={saving}>
            {t('starter.emptyCalendar')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
